import { FilterPanelBaseFilter } from "app/pages/sales-intelligence/modules/advanced-search-filtering/models/BaseFilter";
import type { IFilterInstance } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import { SdkUpgradeView } from "app/pages/sales-intelligence/modules/apps-search-filters/components/upgrade-views/SdkUpgradeView";
import { appOperatingSystemMap } from "app/pages/sales-intelligence/modules/apps-search-filters/types/commonFiltersDto";
import createAdsSellersFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/AdsSellersFilter/factory";
import createCategoryFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/CategoryFilter/factory";
import createIabCategoriesFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/IabCategoriesFilter/factory";
import createListedAddressFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/ListedAddressFilter/factory";
import createPermissionsFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/PermissionsFilter/factory";
import createPriceFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/PriceFilter/factory";
import FindAppsRatingFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/RatingFilter";
import createUpdateDateFilter from "app/pages/sales-intelligence/modules/find-apps-filters/models/UpdateDateFilter/factory";
import type {
    AppsIosIdentifiersDescriptionDto,
    AppsSDKsFilterDescriptionDto,
    FindAppsDescriptionDto,
} from "app/pages/sales-intelligence/modules/find-apps-filters/types/description.dto";
import { getSdkFilterByPlatform } from "app/pages/sales-intelligence/modules/find-apps-filters/utils";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import type {
    IAppsIosFiltersDto,
    IAppsIosIdentifierFilter,
    IAppsSDKsFilterItem,
} from "app/pages/sales-intelligence/store/find-apps/types";

import AppSdkFilter from "../components/filters/AppSdkFilter/AppSdkFilter";
import AppSdkFilterResult from "../components/filters/AppSdkFilter/AppSdkFilterResult";
import { AppsIosIdentifiersFilter } from "../components/filters/AppsIdentifiersFilter/ios/AppsIosIdentifiersFilter";
import { AppsIosIdentifiersFilterResult } from "../components/filters/AppsIdentifiersFilter/ios/AppsIosIdentifiersFilterResult";
import { AppsRatingFilter } from "../components/filters/AppsRatingFilter/AppsRatingFilter";
import { AppsRatingFilterResult } from "../components/filters/AppsRatingFilter/AppsRatingFilterResult";
import createBusinessModelFilter from "../models/BusinessModelFilter/factory";
import createIosDownloadAndEngagementFilter from "../models/IosDownloadAndEngagementFilter/factory";

export const createIosFilterConfig = (description?: FindAppsDescriptionDto): IFilterInstance<IAppsIosFiltersDto>[] => {
    if (!description) {
        return [];
    }

    return [
        createCategoryFilter(description),
        createIabCategoriesFilter(description),
        createUpdateDateFilter(description),
        {
            key: "sdks",
            trackingName: "App SDKs",
            title: { key: "si.find_apps.filters.sdks.title" },
            tooltipText: { key: "si.find_apps.filters.sdks.tooltip" },
            gateStatus: !salesSettingsHelper.isAppsSdksAvailable
                ? { type: "upgrade", view: SdkUpgradeView }
                : undefined,
            filter: new FilterPanelBaseFilter<IAppsSDKsFilterItem[], AppsSDKsFilterDescriptionDto>({
                options: getSdkFilterByPlatform(description?.sdksFilter, appOperatingSystemMap.ios) || {
                    sdksDescriptions: [],
                    inclusionOptions: [],
                    operatorOptions: [],
                },
            }),
            filterView: AppSdkFilter,
            filterResultView: AppSdkFilterResult,
        },
        createAdsSellersFilter(description.iosAdsSellers),
        createPermissionsFilter(description.iosPermission),
        createListedAddressFilter(),
        createIosDownloadAndEngagementFilter(description),
        {
            key: "rating",
            trackingName: "App Rating",
            title: { key: "si.find_apps.filters.rating.title" },
            tooltipText: { key: "si.find_apps.filters.rating.tooltip" },
            filter: new FindAppsRatingFilter({
                options: description?.rating || {
                    numberOfRatingsBuckets: [],
                },
            }),
            filterView: AppsRatingFilter,
            filterResultView: AppsRatingFilterResult,
        },
        // TODO remove when BE fix is released
        // @ts-expect-error incorrect BE model
        createBusinessModelFilter(description.appBusinessModels ?? description.businessModels),
        createPriceFilter(description),
        {
            key: "identifier",
            trackingName: "App Identifier",
            title: { key: "si.find_apps.filters.identifier.title" },
            tooltipText: { key: "si.find_apps.filters.identifier.ios.tooltip" },
            filter: new FilterPanelBaseFilter<IAppsIosIdentifierFilter, AppsIosIdentifiersDescriptionDto>({
                options: description?.iosIdentifiers || {
                    identifiers: ["bundleIds", "trackIds"],
                },
            }),
            filterView: AppsIosIdentifiersFilter,
            filterResultView: AppsIosIdentifiersFilterResult,
        },
    ];
};
